// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useCallback, useEffect, useState } from "react";
import { App, Button, Divider, Space, Switch, Tag, Typography } from "antd";
import { BellOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Homeserver } from "../api";
import { isNativeApp } from "../server";
import { isSoundEnabled, setSoundEnabled } from "../calltones";
import { batteryExempt, disableNtfyPush, getPushStatus, isPushEnabled, registerNtfyPush, requestBatteryExempt, setPushEnabled } from "../push-ntfy";

const hs = new Homeserver();

// A labelled on/off row.
function ToggleRow({ label, checked, loading, onChange }: { readonly label: string; readonly checked: boolean; readonly loading?: boolean; readonly onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
      <Switch checked={checked} loading={loading} onChange={onChange} />
      <span>{label}</span>
    </div>
  );
}

// NotificationsPanel: sound cues (web + native) plus, on the native app, push notifications —
// the on/off toggle that starts/stops the embedded foreground service, a self-test, and the
// battery-optimization status that otherwise freezes push.
export function NotificationsPanel({ token, userId }: { readonly token: string; readonly userId: string }) {
  const { message } = App.useApp();
  const native = isNativeApp();

  // Sound preferences (apply everywhere).
  const [callSound, setCallSound] = useState(isSoundEnabled("call"));
  const [msgSound, setMsgSound] = useState(isSoundEnabled("message"));

  // Push (native only).
  const [on, setOn] = useState(isPushEnabled());
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(getPushStatus().state);
  const [exempt, setExempt] = useState<boolean | null>(null);

  const checkBattery = useCallback(() => {
    void batteryExempt().then(setExempt);
  }, []);

  useEffect(() => {
    if (!native) {
      return;
    }
    checkBattery();
    const onVisible = () => {
      if (!document.hidden) {
        checkBattery();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [native, checkBattery]);

  const togglePush = async (next: boolean) => {
    setBusy(true);
    setOn(next);
    try {
      if (next) {
        setPushEnabled(true);
        await registerNtfyPush(token, userId);
        if (getPushStatus().state === "ok") {
          message.success("Notifications enabled");
        }
        checkBattery();
      } else {
        await disableNtfyPush(token);
        message.info("Notifications turned off");
      }
    } finally {
      setStatus(getPushStatus().state);
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await hs.testPush(token);
      message.success("Test sent — you should see a notification shortly");
    } catch {
      message.error("Couldn't send the test — is the server reachable?");
    } finally {
      setTesting(false);
    }
  };

  const pushHint = (() => {
    if (!on) {
      return "Off — this device won't be woken for new messages or calls.";
    }
    if (status === "rejected") {
      return "On, but the homeserver rejected this device — its ntfy server may be misconfigured.";
    }
    if (status === "error" || status === "disabled") {
      return "On, but the push service couldn't start — check the battery setting below.";
    }
    return "On — a small background service wakes the app for messages and calls. No Google, no extra app.";
  })();

  return (
    <div style={{ marginBottom: 8 }}>
      <Typography.Title level={5}>Sounds</Typography.Title>
      <ToggleRow
        label="Message sounds (a ding when a message arrives)"
        checked={msgSound}
        onChange={(v) => {
          setMsgSound(v);
          setSoundEnabled("message", v);
        }}
      />
      <ToggleRow
        label="Call sounds (ringtone, join / leave, reconnecting)"
        checked={callSound}
        onChange={(v) => {
          setCallSound(v);
          setSoundEnabled("call", v);
        }}
      />

      {native && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Typography.Title level={5}>Push notifications</Typography.Title>
          <ToggleRow label={on ? "Push notifications on" : "Push notifications off"} checked={on} loading={busy} onChange={(v) => void togglePush(v)} />
          <Typography.Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 12 }}>
            {pushHint}
          </Typography.Paragraph>

          {on && (
            <>
              <Space style={{ marginBottom: 12 }} wrap>
                <Button icon={<BellOutlined />} loading={testing} onClick={() => void sendTest()}>
                  Send test notification
                </Button>
              </Space>
              {exempt !== null && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <ThunderboltOutlined />
                  <span>Battery</span>
                  {exempt ? (
                    <Tag color="green">Unrestricted</Tag>
                  ) : (
                    <>
                      <Tag color="warning">Optimized — may delay or drop notifications</Tag>
                      <Button size="small" type="primary" onClick={() => void requestBatteryExempt()}>
                        Allow background
                      </Button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
